import React, { useRef, useEffect, useState } from 'react';
import { FiEdit2, FiSquare, FiCircle, FiType, FiTrash2, FiSave, FiImage, FiMinus, FiMove, FiArrowRight, FiRotateCcw, FiZoomIn, FiHexagon } from 'react-icons/fi';
import { API_URL } from '../config';
import { getAuthToken } from '../services/api';
import axios from 'axios';
import { fabric } from 'fabric';

const getAuthConfig = async () => {
  const token = await getAuthToken();
  return { headers: token ? { Authorization: `Bearer ${token}` } : {} };
};

const TOOLS = {
  SELECT: 'select',
  DRAW: 'draw',
  LINE: 'line',
  RECT: 'rect',
  CIRCLE: 'circle',
  TEXT: 'text',
  MEASURE: 'measure',
  AREA: 'area',
  CALIBRATE: 'calibrate'
};

const COLORS = ['#dc2626', '#2563eb', '#16a34a', '#f59e0b', '#7c3aed', '#000000', '#ffffff'];

// Build a 10x10 diagonal-line hatch pattern in the given color
const makeHatchPattern = (color) => {
  const c = document.createElement('canvas');
  c.width = 10; c.height = 10;
  const ctx = c.getContext('2d');
  ctx.strokeStyle = color;
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.moveTo(-2, 12); ctx.lineTo(12, -2);
  ctx.moveTo(0, 14); ctx.lineTo(14, 0);
  ctx.stroke();
  return new fabric.Pattern({ source: c, repeat: 'repeat' });
};

// Convert #rrggbb hex to rgba string with given alpha
const hexToRgba = (hex, alpha) => {
  const h = hex.replace('#', '');
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

// Distance between two points (for "close polygon by clicking first vertex")
const distance = (a, b) => Math.sqrt((a.x - b.x) ** 2 + (a.y - b.y) ** 2);

// ----- Editable-polygon-vertex machinery (standard fabric.js recipe) -----
// Source: fabric.js docs on customizing controls for polygons.
const polygonPositionHandler = function(dim, finalMatrix, fabricObject) {
  const x = (fabricObject.points[this.pointIndex].x - fabricObject.pathOffset.x);
  const y = (fabricObject.points[this.pointIndex].y - fabricObject.pathOffset.y);
  return fabric.util.transformPoint(
    { x, y },
    fabric.util.multiplyTransformMatrices(
      fabricObject.canvas.viewportTransform,
      fabricObject.calcTransformMatrix()
    )
  );
};

const polygonActionHandler = function(eventData, transform, x, y) {
  const polygon = transform.target;
  const currentControl = polygon.controls[polygon.__corner];
  const mouseLocalPosition = polygon.toLocalPoint(new fabric.Point(x, y), 'center', 'center');
  const polygonBaseSize = polygon._getNonTransformedDimensions();
  const size = polygon._getTransformedDimensions(0, 0);
  polygon.points[currentControl.pointIndex] = {
    x: mouseLocalPosition.x * polygonBaseSize.x / size.x + polygon.pathOffset.x,
    y: mouseLocalPosition.y * polygonBaseSize.y / size.y + polygon.pathOffset.y,
  };
  return true;
};

const polygonAnchorWrapper = (anchorIndex, fn) => function(eventData, transform, x, y) {
  const fabricObject = transform.target;
  const absolutePoint = fabric.util.transformPoint({
    x: (fabricObject.points[anchorIndex].x - fabricObject.pathOffset.x),
    y: (fabricObject.points[anchorIndex].y - fabricObject.pathOffset.y),
  }, fabricObject.calcTransformMatrix());
  const actionPerformed = fn(eventData, transform, x, y);
  fabricObject._setPositionDimensions({});
  const polygonBaseSize = fabricObject._getNonTransformedDimensions();
  const newX = (fabricObject.points[anchorIndex].x - fabricObject.pathOffset.x) / polygonBaseSize.x;
  const newY = (fabricObject.points[anchorIndex].y - fabricObject.pathOffset.y) / polygonBaseSize.y;
  fabricObject.setPositionByOrigin(absolutePoint, newX + 0.5, newY + 0.5);
  return actionPerformed;
};

const makePolygonEditable = (polygon) => {
  polygon.cornerStyle = 'circle';
  polygon.cornerColor = '#2563eb';
  polygon.cornerStrokeColor = '#ffffff';
  polygon.cornerSize = 11;
  polygon.transparentCorners = false;
  polygon.hasBorders = false;
  polygon.objectCaching = false;
  const lastControl = polygon.points.length - 1;
  polygon.controls = polygon.points.reduce((acc, _pt, index) => {
    acc['p' + index] = new fabric.Control({
      positionHandler: polygonPositionHandler,
      actionHandler: polygonAnchorWrapper(index > 0 ? index - 1 : lastControl, polygonActionHandler),
      actionName: 'modifyPolygon',
      pointIndex: index,
    });
    return acc;
  }, {});
};

// Compute the on-canvas area of a polygon (in pixel²) using its actual transformed vertex positions.
const computePolygonPixelArea = (polygon) => {
  const matrix = polygon.calcTransformMatrix();
  const pts = polygon.points.map(p => fabric.util.transformPoint(
    new fabric.Point(p.x - polygon.pathOffset.x, p.y - polygon.pathOffset.y),
    matrix
  ));
  let a = 0;
  for (let i = 0; i < pts.length; i++) {
    const j = (i + 1) % pts.length;
    a += pts[i].x * pts[j].y;
    a -= pts[j].x * pts[i].y;
  }
  return { area: Math.abs(a / 2), centroid: {
    x: pts.reduce((s, p) => s + p.x, 0) / pts.length,
    y: pts.reduce((s, p) => s + p.y, 0) / pts.length,
  } };
};

// Build a fabric.Text label for one polygon edge — positioned at the edge midpoint,
// rotated to follow the edge, kept upright for readability.
const buildSegmentLabel = (a, b, scale, areaId, index) => {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const lengthPx = Math.sqrt(dx * dx + dy * dy);
  let angle = Math.atan2(dy, dx) * 180 / Math.PI;
  if (angle > 90) angle -= 180;
  if (angle < -90) angle += 180;

  let text;
  if (scale) {
    const ft = lengthPx / scale;
    text = ft >= 5280 ? `${(ft / 5280).toFixed(2)} mi` : `${Math.round(ft)} ft`;
  } else {
    text = `${Math.round(lengthPx)} px`;
  }

  const label = new fabric.Text(text, {
    left: (a.x + b.x) / 2, top: (a.y + b.y) / 2, fontSize: 11,
    fill: '#111', fontWeight: '600',
    backgroundColor: 'rgba(255,255,255,0.85)',
    padding: 2, originX: 'center', originY: 'center',
    angle, selectable: false, evented: false,
  });
  label._segmentLabelFor = areaId;
  label._segmentIndex = index;
  return label;
};

// Build labels for every edge of a polygon (in canvas-absolute coords)
const buildSegmentLabelsForPolygon = (polygon, scale) => {
  const matrix = polygon.calcTransformMatrix();
  const pts = polygon.points.map(p => fabric.util.transformPoint(
    new fabric.Point(p.x - polygon.pathOffset.x, p.y - polygon.pathOffset.y),
    matrix
  ));
  const labels = [];
  for (let i = 0; i < pts.length; i++) {
    labels.push(buildSegmentLabel(pts[i], pts[(i + 1) % pts.length], scale, polygon._areaId, i));
  }
  return labels;
};

const formatAreaLabel = (pxArea, scale) => {
  if (scale) {
    const sqFt = pxArea / (scale * scale);
    const acres = sqFt / 43560;
    return acres >= 0.1
      ? `${acres.toFixed(2)} ac\n(${Math.round(sqFt).toLocaleString()} sq ft)`
      : `${Math.round(sqFt).toLocaleString()} sq ft`;
  }
  return `${Math.round(pxArea).toLocaleString()} px²\n(set scale)`;
};

function SketchEditor({ quoteId, sketchId, canvasJson, backgroundUrl, onSave, onClose }) {
  const canvasRef = useRef(null);
  const fabricRef = useRef(null);
  const wrapperRef = useRef(null);
  const bgInputRef = useRef(null);
  const [activeTool, setActiveTool] = useState(TOOLS.SELECT);
  const [activeColor, setActiveColor] = useState('#dc2626');
  const [lineWidth, setLineWidth] = useState(3);
  const [saving, setSaving] = useState(false);
  const [canvasReady, setCanvasReady] = useState(false);
  const [scale, setScale] = useState(null);
  const [showCalibrate, setShowCalibrate] = useState(false);
  const [calibratePixels, setCalibratePixels] = useState(0);
  const [calibrateDistance, setCalibrateDistance] = useState('');
  // Area tool config (applied to new areas as they're drawn)
  const [areaOpacity, setAreaOpacity] = useState(0.4);
  const [useHatch, setUseHatch] = useState(false);
  const [showSegmentLabels, setShowSegmentLabels] = useState(true);
  // Currently selected fabric object (for the per-shape opacity slider)
  const [selectedShape, setSelectedShape] = useState(null);
  // In-progress polygon vertices when drawing an area
  const polygonPoints = useRef([]);
  const polygonPreview = useRef(null);
  const vertexMarkers = useRef([]);
  const [calibrateUnit, setCalibrateUnit] = useState('ft');

  const isDrawing = useRef(false);
  const startPoint = useRef(null);
  const activeShape = useRef(null);

  // Initialize canvas
  useEffect(() => {
    // Size to fit the available viewport — leaves room for toolbar, footer, and modal padding.
    const canvasWidth = Math.max(600, Math.min(1400, window.innerWidth - 80));
    const canvasHeight = Math.max(450, Math.min(900, window.innerHeight - 220));
    const canvas = new fabric.Canvas(canvasRef.current, {
      width: canvasWidth,
      height: canvasHeight,
      backgroundColor: '#ffffff',
      selection: true
    });
    fabricRef.current = canvas;

    // Load background
    if (backgroundUrl) {
      loadBgFromUrl(canvas, backgroundUrl);
    }

    // Load existing canvas data
    if (canvasJson && canvasJson !== '{}') {
      try {
        const parsed = typeof canvasJson === 'string' ? JSON.parse(canvasJson) : canvasJson;
        if (parsed._scale) setScale(parsed._scale);
        canvas.loadFromJSON(parsed, () => {
          // Reattach editable vertex controls to area polygons that came back from JSON
          canvas.getObjects().forEach(o => {
            if (o.type === 'polygon' && o._isArea) makePolygonEditable(o);
          });
          canvas.renderAll();
        });
      } catch (e) {
        console.error('Failed to load canvas JSON:', e);
      }
    }

    setCanvasReady(true);

    return () => {
      canvas.dispose();
    };
  }, []);

  const loadBgFromUrl = async (canvas, url) => {
    try {
      let imgUrl = url;
      if (url.startsWith('/api')) {
        const token = await getAuthToken();
        imgUrl = `${API_URL.replace('/api', '')}${url}${url.includes('?') ? '&' : '?'}token=${token}`;
      }
      fabric.Image.fromURL(imgUrl, (img) => {
        if (!img) return;
        const s = Math.min(canvas.getWidth() / img.width, canvas.getHeight() / img.height);
        img.set({ scaleX: s, scaleY: s, originX: 'left', originY: 'top' });
        canvas.setBackgroundImage(img, canvas.renderAll.bind(canvas));
      }, { crossOrigin: 'anonymous' });
    } catch (e) {
      console.error('Failed to load background:', e);
    }
  };

  const loadBgFromDataUrl = (canvas, dataUrl) => {
    fabric.Image.fromURL(dataUrl, (img) => {
      if (!img) return;
      const s = Math.min(canvas.getWidth() / img.width, canvas.getHeight() / img.height);
      img.set({ scaleX: s, scaleY: s, originX: 'left', originY: 'top' });
      canvas.setBackgroundImage(img, canvas.renderAll.bind(canvas));
    });
  };

  // Track selected shape so we can show a per-shape opacity slider in the toolbar
  useEffect(() => {
    const canvas = fabricRef.current;
    if (!canvas) return;
    const onSel = (e) => {
      const obj = e.selected ? e.selected[0] : canvas.getActiveObject();
      setSelectedShape(obj || null);
    };
    const onClear = () => setSelectedShape(null);
    canvas.on('selection:created', onSel);
    canvas.on('selection:updated', onSel);
    canvas.on('selection:cleared', onClear);
    return () => {
      canvas.off('selection:created', onSel);
      canvas.off('selection:updated', onSel);
      canvas.off('selection:cleared', onClear);
    };
  }, [canvasReady]);

  // Cancel an in-progress polygon (called when leaving Area tool or hitting Esc)
  const cancelPolygon = () => {
    const canvas = fabricRef.current;
    if (!canvas) return;
    if (polygonPreview.current) { canvas.remove(polygonPreview.current); polygonPreview.current = null; }
    vertexMarkers.current.forEach(m => canvas.remove(m));
    vertexMarkers.current = [];
    polygonPoints.current = [];
    canvas.renderAll();
  };

  // Focus wrapper for paste
  useEffect(() => {
    if (wrapperRef.current) wrapperRef.current.focus();
  }, [canvasReady]);

  // Tool handling
  useEffect(() => {
    const canvas = fabricRef.current;
    if (!canvas) return;

    canvas.isDrawingMode = activeTool === TOOLS.DRAW;
    canvas.selection = activeTool === TOOLS.SELECT;

    if (activeTool === TOOLS.DRAW) {
      canvas.freeDrawingBrush.color = activeColor;
      canvas.freeDrawingBrush.width = lineWidth;
    }

    canvas.off('mouse:down');
    canvas.off('mouse:move');
    canvas.off('mouse:up');
    canvas.off('mouse:dblclick');

    // If we were drawing a polygon and switched away, abandon it
    if (activeTool !== TOOLS.AREA && polygonPoints.current.length > 0) {
      cancelPolygon();
    }

    if ([TOOLS.LINE, TOOLS.MEASURE, TOOLS.CALIBRATE].includes(activeTool)) {
      canvas.on('mouse:down', (opt) => {
        const pointer = canvas.getPointer(opt.e);
        isDrawing.current = true;
        startPoint.current = pointer;
        const color = activeTool === TOOLS.CALIBRATE ? '#f59e0b' : activeColor;
        const dash = activeTool === TOOLS.CALIBRATE ? [8, 4] : undefined;
        const line = new fabric.Line([pointer.x, pointer.y, pointer.x, pointer.y], {
          stroke: color,
          strokeWidth: activeTool === TOOLS.CALIBRATE ? 2 : lineWidth,
          strokeDashArray: dash,
          selectable: true,
          evented: true
        });
        canvas.add(line);
        activeShape.current = line;
      });
      canvas.on('mouse:move', (opt) => {
        if (!isDrawing.current || !activeShape.current) return;
        const pointer = canvas.getPointer(opt.e);
        activeShape.current.set({ x2: pointer.x, y2: pointer.y });
        canvas.renderAll();
      });
      canvas.on('mouse:up', (opt) => {
        if (!isDrawing.current) return;
        isDrawing.current = false;
        const pointer = canvas.getPointer(opt.e);
        const dx = pointer.x - startPoint.current.x;
        const dy = pointer.y - startPoint.current.y;
        const pixels = Math.sqrt(dx * dx + dy * dy);

        if (activeTool === TOOLS.CALIBRATE) {
          canvas.remove(activeShape.current);
          setCalibratePixels(pixels);
          setShowCalibrate(true);
        }

        if (activeTool === TOOLS.MEASURE) {
          let labelText;
          if (scale) {
            const feet = pixels / scale;
            labelText = feet >= 5280 ? `${(feet / 5280).toFixed(2)} mi` : `${Math.round(feet * 10) / 10} ft`;
          } else {
            labelText = `${Math.round(pixels)}px (set scale first)`;
          }
          const midX = (startPoint.current.x + pointer.x) / 2;
          const midY = (startPoint.current.y + pointer.y) / 2;
          const label = new fabric.Text(labelText, {
            left: midX, top: midY - 15, fontSize: 14,
            fill: activeColor, fontWeight: 'bold',
            backgroundColor: 'rgba(255,255,255,0.85)',
            padding: 4, selectable: true
          });
          canvas.add(label);
        }

        activeShape.current = null;
        startPoint.current = null;
        canvas.renderAll();
      });
    }

    if (activeTool === TOOLS.RECT) {
      canvas.on('mouse:down', (opt) => {
        const pointer = canvas.getPointer(opt.e);
        isDrawing.current = true;
        startPoint.current = pointer;
        const rect = new fabric.Rect({
          left: pointer.x, top: pointer.y, width: 0, height: 0,
          fill: 'transparent', stroke: activeColor, strokeWidth: lineWidth, selectable: true
        });
        canvas.add(rect);
        activeShape.current = rect;
      });
      canvas.on('mouse:move', (opt) => {
        if (!isDrawing.current || !activeShape.current) return;
        const pointer = canvas.getPointer(opt.e);
        activeShape.current.set({
          width: Math.abs(pointer.x - startPoint.current.x),
          height: Math.abs(pointer.y - startPoint.current.y),
          left: Math.min(pointer.x, startPoint.current.x),
          top: Math.min(pointer.y, startPoint.current.y)
        });
        canvas.renderAll();
      });
      canvas.on('mouse:up', () => { isDrawing.current = false; activeShape.current = null; });
    }

    if (activeTool === TOOLS.CIRCLE) {
      canvas.on('mouse:down', (opt) => {
        const pointer = canvas.getPointer(opt.e);
        isDrawing.current = true;
        startPoint.current = pointer;
        const circle = new fabric.Circle({
          left: pointer.x, top: pointer.y, radius: 0,
          fill: 'transparent', stroke: activeColor, strokeWidth: lineWidth, selectable: true
        });
        canvas.add(circle);
        activeShape.current = circle;
      });
      canvas.on('mouse:move', (opt) => {
        if (!isDrawing.current || !activeShape.current) return;
        const pointer = canvas.getPointer(opt.e);
        const radius = Math.sqrt(
          Math.pow(pointer.x - startPoint.current.x, 2) + Math.pow(pointer.y - startPoint.current.y, 2)
        ) / 2;
        activeShape.current.set({ radius });
        canvas.renderAll();
      });
      canvas.on('mouse:up', () => { isDrawing.current = false; activeShape.current = null; });
    }

    if (activeTool === TOOLS.TEXT) {
      canvas.on('mouse:down', (opt) => {
        const pointer = canvas.getPointer(opt.e);
        const text = prompt('Enter text:');
        if (!text) return;
        const textObj = new fabric.IText(text, {
          left: pointer.x, top: pointer.y, fontSize: 18,
          fill: activeColor, fontWeight: 'bold', selectable: true
        });
        canvas.add(textObj);
        canvas.renderAll();
        setActiveTool(TOOLS.SELECT);
      });
    }

    if (activeTool === TOOLS.AREA) {
      // Click vertices; click within 12px of first vertex (or dblclick) to close.
      canvas.on('mouse:down', (opt) => {
        const pointer = canvas.getPointer(opt.e);
        const pts = polygonPoints.current;

        // Closing click? (near first vertex, ≥3 vertices already)
        if (pts.length >= 3 && distance(pointer, pts[0]) < 12) {
          finalizeArea();
          return;
        }

        pts.push({ x: pointer.x, y: pointer.y });

        // Mark the vertex visually (extra emphasis on the first so user knows where to close)
        const isFirst = pts.length === 1;
        const marker = new fabric.Circle({
          left: pointer.x, top: pointer.y, radius: isFirst ? 6 : 4,
          fill: isFirst ? '#ffffff' : activeColor,
          stroke: activeColor, strokeWidth: 2,
          originX: 'center', originY: 'center',
          selectable: false, evented: false, excludeFromExport: true,
        });
        canvas.add(marker);
        vertexMarkers.current.push(marker);

        // Update preview polygon (or polyline if only 2 pts)
        if (polygonPreview.current) canvas.remove(polygonPreview.current);
        if (pts.length >= 2) {
          polygonPreview.current = new fabric.Polyline([...pts], {
            stroke: activeColor, strokeWidth: lineWidth,
            fill: 'transparent', selectable: false, evented: false,
            excludeFromExport: true,
          });
          canvas.add(polygonPreview.current);
        }
        canvas.renderAll();
      });

      canvas.on('mouse:dblclick', () => {
        if (polygonPoints.current.length >= 3) finalizeArea();
      });
    }
  }, [activeTool, activeColor, lineWidth, scale, areaOpacity, useHatch]);

  // Close the in-progress area polygon, calculate its area, and add a labeled shape
  const finalizeArea = () => {
    const canvas = fabricRef.current;
    const pts = polygonPoints.current;
    if (!canvas || pts.length < 3) return;

    // Remove preview + vertex markers
    if (polygonPreview.current) { canvas.remove(polygonPreview.current); polygonPreview.current = null; }
    vertexMarkers.current.forEach(m => canvas.remove(m));
    vertexMarkers.current = [];

    // Shoelace formula → pixel² area
    let pxArea = 0;
    for (let i = 0; i < pts.length; i++) {
      const j = (i + 1) % pts.length;
      pxArea += pts[i].x * pts[j].y;
      pxArea -= pts[j].x * pts[i].y;
    }
    pxArea = Math.abs(pxArea / 2);

    // Convert to ft² / acres if scale set
    let labelText;
    if (scale) {
      const sqFt = pxArea / (scale * scale);
      const acres = sqFt / 43560;
      labelText = acres >= 0.1
        ? `${acres.toFixed(2)} ac\n(${Math.round(sqFt).toLocaleString()} sq ft)`
        : `${Math.round(sqFt).toLocaleString()} sq ft`;
    } else {
      labelText = `${Math.round(pxArea).toLocaleString()} px²\n(set scale)`;
    }

    const fill = useHatch ? makeHatchPattern(activeColor) : hexToRgba(activeColor, areaOpacity);
    const polygon = new fabric.Polygon([...pts], {
      fill,
      stroke: activeColor,
      strokeWidth: lineWidth,
      opacity: useHatch ? areaOpacity : 1,
      objectCaching: false,
      selectable: true,
    });
    const areaId = 'a_' + Math.random().toString(36).slice(2, 10);
    polygon._isArea = true;
    polygon._areaId = areaId;
    polygon._areaColor = activeColor;
    polygon._useHatch = useHatch;
    makePolygonEditable(polygon);
    canvas.add(polygon);

    // Centroid for the label
    const cx = pts.reduce((s, p) => s + p.x, 0) / pts.length;
    const cy = pts.reduce((s, p) => s + p.y, 0) / pts.length;
    const label = new fabric.Text(labelText, {
      left: cx, top: cy, fontSize: 14,
      fill: '#111', fontWeight: 'bold',
      backgroundColor: 'rgba(255,255,255,0.85)',
      padding: 4, originX: 'center', originY: 'center',
      textAlign: 'center', selectable: true,
    });
    label._areaLabelFor = areaId;
    canvas.add(label);

    // Per-edge distance labels
    buildSegmentLabelsForPolygon(polygon, scale).forEach(l => {
      l.visible = showSegmentLabels;
      canvas.add(l);
    });

    polygonPoints.current = [];
    canvas.renderAll();
  };

  // When an editable area polygon is reshaped/moved/scaled: recompute its area
  // label AND rebuild its segment labels (easier than mutating each in place).
  useEffect(() => {
    const canvas = fabricRef.current;
    if (!canvas) return;
    const onModified = (e) => {
      const obj = e.target;
      if (!obj || !obj._isArea || !obj._areaId) return;

      // Update area label
      const areaLabel = canvas.getObjects().find(o => o._areaLabelFor === obj._areaId);
      if (areaLabel) {
        const { area, centroid } = computePolygonPixelArea(obj);
        areaLabel.set({ text: formatAreaLabel(area, scale), left: centroid.x, top: centroid.y });
      }

      // Rebuild segment labels
      const oldSegLabels = canvas.getObjects().filter(o => o._segmentLabelFor === obj._areaId);
      oldSegLabels.forEach(l => canvas.remove(l));
      buildSegmentLabelsForPolygon(obj, scale).forEach(l => {
        l.visible = showSegmentLabels;
        canvas.add(l);
      });

      canvas.renderAll();
    };
    canvas.on('object:modified', onModified);
    return () => canvas.off('object:modified', onModified);
  }, [canvasReady, scale, showSegmentLabels]);

  // Toggle segment label visibility globally
  useEffect(() => {
    const canvas = fabricRef.current;
    if (!canvas) return;
    canvas.getObjects().forEach(o => {
      if (o._segmentLabelFor) o.visible = showSegmentLabels;
    });
    canvas.renderAll();
  }, [showSegmentLabels, canvasReady]);

  // Adjust opacity of currently-selected shape
  const updateSelectedOpacity = (newOpacity) => {
    const canvas = fabricRef.current;
    if (!canvas || !selectedShape) return;
    if (selectedShape._isArea && !selectedShape._useHatch) {
      // Solid-fill area: change the rgba alpha directly so the stroke stays solid
      selectedShape.set({ fill: hexToRgba(selectedShape._areaColor || activeColor, newOpacity) });
    } else {
      // Anything else (hatch areas, lines, text, etc.): set whole-object opacity
      selectedShape.set({ opacity: newOpacity });
    }
    canvas.renderAll();
  };

  // Save
  const handleSave = async () => {
    const canvas = fabricRef.current;
    if (!canvas) return;
    setSaving(true);
    try {
      const canvasData = canvas.toJSON(['_isArea', '_areaId', '_areaColor', '_useHatch', '_areaLabelFor', '_segmentLabelFor', '_segmentIndex']);
      if (scale) canvasData._scale = scale;
      const json = JSON.stringify(canvasData);
      const imageData = canvas.toDataURL({ format: 'png', quality: 0.8 });
      const config = await getAuthConfig();
      await axios.put(`${API_URL}/quotes/${quoteId}/sketches/${sketchId}`, { canvas_json: json }, config);
      await axios.post(`${API_URL}/quotes/${quoteId}/sketches/${sketchId}/save-image`, { image_data: imageData }, config);
      if (onSave) onSave();
    } catch (error) {
      alert('Failed to save sketch');
      console.error(error);
    }
    setSaving(false);
  };

  const handleDeleteSelected = () => {
    const canvas = fabricRef.current;
    if (!canvas) return;
    const active = canvas.getActiveObjects();
    active.forEach(obj => canvas.remove(obj));
    canvas.discardActiveObject();
    canvas.renderAll();
  };

  const handleClear = () => {
    if (!window.confirm('Clear all drawings? This cannot be undone.')) return;
    const canvas = fabricRef.current;
    if (!canvas) return;
    canvas.clear();
    canvas.backgroundColor = '#ffffff';
    canvas.renderAll();
  };

  const handleBackgroundUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    // Show on canvas immediately
    const reader = new FileReader();
    reader.onload = (event) => {
      const canvas = fabricRef.current;
      if (canvas) loadBgFromDataUrl(canvas, event.target.result);
    };
    reader.readAsDataURL(file);

    // Upload to server
    const formData = new FormData();
    formData.append('file', file);
    try {
      const config = await getAuthConfig();
      config.headers['Content-Type'] = 'multipart/form-data';
      await axios.post(`${API_URL}/quotes/${quoteId}/sketches/${sketchId}/background`, formData, config);
    } catch (error) {
      alert('Failed to upload background');
    }
  };

  const handlePaste = async (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (const item of items) {
      if (item.type.startsWith('image/')) {
        e.preventDefault();
        const blob = item.getAsFile();
        const reader = new FileReader();
        reader.onload = async (event) => {
          const canvas = fabricRef.current;
          if (canvas) loadBgFromDataUrl(canvas, event.target.result);
          // Upload to server
          try {
            const formData = new FormData();
            formData.append('file', blob, 'screenshot.png');
            const config = await getAuthConfig();
            config.headers['Content-Type'] = 'multipart/form-data';
            await axios.post(`${API_URL}/quotes/${quoteId}/sketches/${sketchId}/background`, formData, config);
          } catch (err) {
            console.error('Failed to upload pasted background:', err);
          }
        };
        reader.readAsDataURL(blob);
        break;
      }
    }
  };

  const toolBtn = (tool, icon, label) => (
    <button
      onClick={() => setActiveTool(tool)}
      title={label}
      style={{
        padding: '8px 10px', border: '1px solid #ddd', borderRadius: '6px',
        backgroundColor: activeTool === tool ? '#2563eb' : 'white',
        color: activeTool === tool ? 'white' : '#374151',
        cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px',
        fontSize: '12px', fontWeight: '500'
      }}
    >
      {icon} {label}
    </button>
  );

  return (
    <div
      ref={wrapperRef}
      tabIndex={0}
      onPaste={handlePaste}
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.7)', display: 'flex',
        alignItems: 'center', justifyContent: 'center', zIndex: 100000, padding: '10px',
        outline: 'none'
      }}
    >
      <div style={{
        backgroundColor: 'white', borderRadius: '12px', overflow: 'hidden',
        maxWidth: '1480px', width: '100%', maxHeight: 'calc(95dvh - 60px)', display: 'flex', flexDirection: 'column'
      }}>
        {/* Toolbar */}
        <div style={{
          padding: '10px 16px', borderBottom: '1px solid #e5e7eb',
          display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center'
        }}>
          {toolBtn(TOOLS.SELECT, <FiMove size={14} />, 'Select')}
          {toolBtn(TOOLS.DRAW, <FiEdit2 size={14} />, 'Draw')}
          {toolBtn(TOOLS.LINE, <FiMinus size={14} />, 'Line')}
          {toolBtn(TOOLS.RECT, <FiSquare size={14} />, 'Rect')}
          {toolBtn(TOOLS.CIRCLE, <FiCircle size={14} />, 'Circle')}
          {toolBtn(TOOLS.TEXT, <FiType size={14} />, 'Text')}
          {toolBtn(TOOLS.MEASURE, <FiArrowRight size={14} />, 'Measure')}
          {toolBtn(TOOLS.AREA, <FiHexagon size={14} />, 'Area')}
          <button
            onClick={() => setShowSegmentLabels(s => !s)}
            title="Show distance on each polygon segment"
            style={{
              padding: '4px 10px', fontSize: '11px', borderRadius: '6px',
              border: '1px solid #d1d5db', cursor: 'pointer',
              backgroundColor: showSegmentLabels ? '#eff6ff' : 'white',
              color: showSegmentLabels ? '#1d4ed8' : '#6b7280', fontWeight: '600',
            }}
          >
            Segment ft {showSegmentLabels ? 'ON' : 'OFF'}
          </button>
          {toolBtn(TOOLS.CALIBRATE, <FiZoomIn size={14} />, scale ? `Scale: ${Math.round(1/scale * 100)/100} ft/px` : 'Set Scale')}

          {/* Area tool config: hatch toggle + new-area opacity */}
          {activeTool === TOOLS.AREA && (
            <>
              <div style={{ width: '1px', height: '24px', backgroundColor: '#e5e7eb', margin: '0 4px' }} />
              <button
                onClick={() => setUseHatch(h => !h)}
                title="Toggle solid fill / hatch pattern"
                style={{
                  padding: '4px 10px', fontSize: '11px', borderRadius: '6px',
                  border: '1px solid #d1d5db', cursor: 'pointer',
                  backgroundColor: useHatch ? '#eff6ff' : 'white',
                  color: useHatch ? '#1d4ed8' : '#374151', fontWeight: '600',
                }}
              >
                {useHatch ? 'Hatch' : 'Solid'}
              </button>
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: '#6b7280' }}>
                Opacity
                <input
                  type="range" min="0.1" max="1" step="0.05"
                  value={areaOpacity}
                  onChange={(e) => setAreaOpacity(parseFloat(e.target.value))}
                  style={{ width: '70px' }}
                />
                <span style={{ width: '28px', textAlign: 'right' }}>{Math.round(areaOpacity * 100)}%</span>
              </label>
              <span style={{ fontSize: '11px', color: '#9ca3af' }}>Click vertices, click first dot to close</span>
            </>
          )}

          {/* Per-shape opacity slider — appears when an existing shape is selected */}
          {selectedShape && activeTool === TOOLS.SELECT && (
            <>
              <div style={{ width: '1px', height: '24px', backgroundColor: '#e5e7eb', margin: '0 4px' }} />
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: '#6b7280' }}>
                Selected opacity
                <input
                  type="range" min="0.05" max="1" step="0.05"
                  defaultValue={
                    selectedShape._isArea && !selectedShape._useHatch && typeof selectedShape.fill === 'string' && selectedShape.fill.startsWith('rgba')
                      ? (parseFloat(selectedShape.fill.split(',')[3]) || 1)
                      : (selectedShape.opacity ?? 1)
                  }
                  onChange={(e) => updateSelectedOpacity(parseFloat(e.target.value))}
                  style={{ width: '90px' }}
                />
              </label>
            </>
          )}

          <div style={{ width: '1px', height: '24px', backgroundColor: '#e5e7eb', margin: '0 4px' }} />

          <div style={{ display: 'flex', gap: '3px', alignItems: 'center' }}>
            {COLORS.map(c => (
              <button
                key={c}
                onClick={() => setActiveColor(c)}
                style={{
                  width: '22px', height: '22px', borderRadius: '50%',
                  border: activeColor === c ? '3px solid #2563eb' : '2px solid #ddd',
                  backgroundColor: c, cursor: 'pointer', padding: 0
                }}
              />
            ))}
          </div>

          <div style={{ width: '1px', height: '24px', backgroundColor: '#e5e7eb', margin: '0 4px' }} />

          <select
            value={lineWidth}
            onChange={(e) => setLineWidth(parseInt(e.target.value))}
            style={{ padding: '4px 8px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '12px' }}
          >
            <option value={1}>Thin</option>
            <option value={3}>Medium</option>
            <option value={5}>Thick</option>
            <option value={8}>Extra Thick</option>
          </select>
        </div>

        {/* Canvas */}
        <div style={{ flex: 1, overflow: 'auto', display: 'flex', justifyContent: 'center', padding: '10px', backgroundColor: '#f3f4f6' }}>
          <canvas ref={canvasRef} />
        </div>

        {/* Bottom bar */}
        <div style={{
          padding: '10px 16px', borderTop: '1px solid #e5e7eb',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px'
        }}>
          <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
            <button onClick={() => bgInputRef.current?.click()}
              title="Upload background image, or paste a screenshot (Ctrl+V)"
              style={{ padding: '8px 14px', border: '1px solid #ddd', borderRadius: '6px', backgroundColor: 'white', cursor: 'pointer', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '4px' }}>
              <FiImage size={14} /> Background
            </button>
            <span style={{ fontSize: '11px', color: '#9ca3af' }}>or Ctrl+V to paste</span>
            <input ref={bgInputRef} type="file" accept="image/*" onChange={handleBackgroundUpload} style={{ display: 'none' }} />

            <button onClick={handleDeleteSelected} style={{
              padding: '8px 14px', border: '1px solid #fecaca', borderRadius: '6px',
              backgroundColor: '#fef2f2', color: '#dc2626', cursor: 'pointer', fontSize: '12px',
              display: 'flex', alignItems: 'center', gap: '4px'
            }}>
              <FiTrash2 size={14} /> Delete
            </button>
            <button onClick={handleClear} style={{
              padding: '8px 14px', border: '1px solid #fecaca', borderRadius: '6px',
              backgroundColor: 'white', color: '#dc2626', cursor: 'pointer', fontSize: '12px',
              display: 'flex', alignItems: 'center', gap: '4px'
            }}>
              <FiRotateCcw size={14} /> Clear All
            </button>
          </div>

          <div style={{ display: 'flex', gap: '8px' }}>
            <button onClick={onClose} style={{
              padding: '8px 20px', border: '1px solid #ddd', borderRadius: '6px',
              backgroundColor: '#f5f5f5', cursor: 'pointer', fontSize: '13px'
            }}>
              Cancel
            </button>
            <button onClick={handleSave} disabled={saving} style={{
              padding: '8px 20px', border: 'none', borderRadius: '6px',
              backgroundColor: '#2563eb', color: 'white', cursor: saving ? 'not-allowed' : 'pointer',
              fontSize: '13px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px',
              opacity: saving ? 0.7 : 1
            }}>
              <FiSave size={14} /> {saving ? 'Saving...' : 'Save Sketch'}
            </button>
          </div>
        </div>
      </div>

      {/* Calibration Dialog */}
      {showCalibrate && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
          alignItems: 'center', justifyContent: 'center', zIndex: 200000
        }}>
          <div style={{
            backgroundColor: 'white', borderRadius: '12px', padding: '24px',
            maxWidth: '400px', width: '90%', textAlign: 'center'
          }}>
            <h3 style={{ margin: '0 0 8px', fontSize: '18px' }}>Set Scale</h3>
            <p style={{ color: '#666', fontSize: '13px', margin: '0 0 16px' }}>
              You drew a reference line of <strong>{Math.round(calibratePixels)} pixels</strong>.<br />
              What is the real-world distance of that line?
            </p>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', marginBottom: '16px' }}>
              <input
                type="number" value={calibrateDistance}
                onChange={(e) => setCalibrateDistance(e.target.value)}
                placeholder="e.g., 200"
                style={{ padding: '10px', border: '1px solid #ddd', borderRadius: '8px', fontSize: '16px', width: '120px', textAlign: 'center' }}
                autoFocus
              />
              <select value={calibrateUnit} onChange={(e) => setCalibrateUnit(e.target.value)}
                style={{ padding: '10px', border: '1px solid #ddd', borderRadius: '8px', fontSize: '16px' }}>
                <option value="ft">feet</option>
                <option value="yd">yards</option>
                <option value="m">meters</option>
              </select>
            </div>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
              <button onClick={() => { setShowCalibrate(false); setCalibrateDistance(''); }}
                style={{ padding: '10px 20px', backgroundColor: '#f5f5f5', border: '1px solid #ddd', borderRadius: '8px', cursor: 'pointer', fontSize: '14px' }}>
                Cancel
              </button>
              <button
                onClick={() => {
                  const dist = parseFloat(calibrateDistance);
                  if (!dist || dist <= 0) { alert('Enter a valid distance'); return; }
                  let feet = dist;
                  if (calibrateUnit === 'yd') feet = dist * 3;
                  if (calibrateUnit === 'm') feet = dist * 3.28084;
                  setScale(calibratePixels / feet);
                  setShowCalibrate(false);
                  setCalibrateDistance('');
                  setActiveTool(TOOLS.MEASURE);
                }}
                disabled={!calibrateDistance}
                style={{
                  padding: '10px 20px', backgroundColor: calibrateDistance ? '#2563eb' : '#ccc',
                  color: 'white', border: 'none', borderRadius: '8px',
                  cursor: calibrateDistance ? 'pointer' : 'not-allowed', fontSize: '14px', fontWeight: '600'
                }}>
                Set Scale
              </button>
            </div>
            {scale && (
              <p style={{ color: '#16a34a', fontSize: '12px', marginTop: '12px' }}>
                Current scale: 1 pixel = {Math.round(1/scale * 100) / 100} ft
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default SketchEditor;
